<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Patient Care</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('ipd-dashboard')">IPD</a></li>
                      <li class="breadcrumb-item active">Patient Care</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="row">
              <div class="col-12 text-center py-5">
                <div class="spinner-border text-primary" style="width: 3rem; height: 3rem;"></div>
                <p class="text-muted mt-3">Loading patient information...</p>
              </div>
            </div>

            <!-- Not Found State -->
            <div v-else-if="!admission" class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body text-center py-5">
                    <i class="mdi mdi-account-question text-warning" style="font-size: 80px;"></i>
                    <h4 class="mt-3">Admission Not Found</h4>
                    <p class="text-muted">The admission record could not be found or may have been discharged.</p>
                    <button class="btn btn-primary" @click="goTo('ipd-dashboard')">
                      <i class="mdi mdi-arrow-left me-1"></i> Back to IPD Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Patient Care Content -->
            <div v-else>
              <!-- Patient Header Card -->
              <div class="row">
                <div class="col-12">
                  <div class="card bg-primary bg-gradient">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-start">
                        <div class="d-flex align-items-center">
                          <div class="avatar-lg me-3">
                              <span class="avatar-title rounded-circle bg-white text-primary font-size-24">
                                {{ getInitials(admission.patientName) }}
                              </span>
                          </div>
                          <div class="text-white">
                            <h4 class="mb-1 text-white">{{ admission.patientName }}</h4>
                            <p class="mb-1">
                              <span class="badge bg-light text-dark me-2">{{ admission.patientNo || admission.patientID }}</span>
                              <span class="me-3"><i class="mdi mdi-gender-male-female me-1"></i>{{ admission.gender || admission.patientGender }}, {{ admission.age || admission.patientAge }} yrs</span>
                              <span v-if="admission.bloodGroup" class="me-3"><i class="mdi mdi-water me-1"></i>{{ admission.bloodGroup }}</span>
                              <span v-if="admission.phone || admission.patientPhone"><i class="mdi mdi-phone me-1"></i>{{ admission.phone || admission.patientPhone }}</span>
                            </p>
                            <p class="mb-0 opacity-75">
                              <i class="mdi mdi-bed me-1"></i> {{ admission.wardName }} - Bed {{ admission.bedNumber }}
                              <span class="mx-2">|</span>
                              <i class="mdi mdi-calendar me-1"></i> Admitted: {{ formatDate(admission.admissionDate) }}
                              <span class="mx-2">|</span>
                              <span class="badge bg-warning text-dark">Day {{ admission.daysAdmitted || 1 }}</span>
                            </p>
                          </div>
                        </div>
                        <div class="text-end">
                          <p class="text-white-50 mb-1">Admission No</p>
                          <h5 class="text-white mb-2">{{ admission.admissionNo || admission.admissionNumber }}</h5>
                          <button class="btn btn-light btn-sm" @click="goTo('ipd-dashboard')">
                            <i class="mdi mdi-arrow-left me-1"></i> Back
                          </button>
                        </div>
                      </div>

                      <!-- Allergies Alert -->
                      <div v-if="admission.allergies" class="alert alert-danger mt-3 mb-0 d-flex align-items-center">
                        <i class="mdi mdi-alert-circle font-size-20 me-2"></i>
                        <div>
                          <strong>Allergies:</strong> {{ admission.allergies }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Quick Stats -->
              <div class="row">
                <div class="col-xl-3 col-md-6">
                  <div class="card mini-stats-wid">
                    <div class="card-body">
                      <div class="d-flex">
                        <div class="flex-grow-1">
                          <p class="text-muted fw-medium mb-2">Vitals Today</p>
                          <h4 class="mb-0">{{ todayVitalsCount }}</h4>
                        </div>
                        <div class="flex-shrink-0 align-self-center">
                          <div class="mini-stat-icon avatar-sm rounded-circle bg-primary">
                            <span class="avatar-title"><i class="mdi mdi-heart-pulse font-size-24"></i></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-xl-3 col-md-6">
                  <div class="card mini-stats-wid">
                    <div class="card-body">
                      <div class="d-flex">
                        <div class="flex-grow-1">
                          <p class="text-muted fw-medium mb-2">Medications</p>
                          <h4 class="mb-0">{{ medications.length }}</h4>
                        </div>
                        <div class="flex-shrink-0 align-self-center">
                          <div class="mini-stat-icon avatar-sm rounded-circle bg-success">
                            <span class="avatar-title bg-success"><i class="mdi mdi-pill font-size-24"></i></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-xl-3 col-md-6">
                  <div class="card mini-stats-wid">
                    <div class="card-body">
                      <div class="d-flex">
                        <div class="flex-grow-1">
                          <p class="text-muted fw-medium mb-2">Nursing Notes</p>
                          <h4 class="mb-0">{{ nursingNotes.length }}</h4>
                        </div>
                        <div class="flex-shrink-0 align-self-center">
                          <div class="mini-stat-icon avatar-sm rounded-circle bg-info">
                            <span class="avatar-title bg-info"><i class="mdi mdi-clipboard-text font-size-24"></i></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-xl-3 col-md-6">
                  <div class="card mini-stats-wid">
                    <div class="card-body">
                      <div class="d-flex">
                        <div class="flex-grow-1">
                          <p class="text-muted fw-medium mb-2">Doctor Orders</p>
                          <h4 class="mb-0">{{ doctorOrders.length }}</h4>
                        </div>
                        <div class="flex-shrink-0 align-self-center">
                          <div class="mini-stat-icon avatar-sm rounded-circle bg-warning">
                            <span class="avatar-title bg-warning"><i class="mdi mdi-stethoscope font-size-24"></i></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tabs Content -->
              <div class="row">
                <div class="col-12">
                  <div class="card">
                    <div class="card-body">
                      <ul class="nav nav-tabs nav-tabs-custom nav-justified">
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'vitals' }" href="javascript:void(0);" @click="activeTab = 'vitals'">
                            <i class="mdi mdi-heart-pulse me-1"></i> Vitals
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'medications' }" href="javascript:void(0);" @click="activeTab = 'medications'">
                            <i class="mdi mdi-pill me-1"></i> Medications
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'notes' }" href="javascript:void(0);" @click="activeTab = 'notes'">
                            <i class="mdi mdi-clipboard-text me-1"></i> Nursing Notes
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'orders' }" href="javascript:void(0);" @click="activeTab = 'orders'">
                            <i class="mdi mdi-stethoscope me-1"></i> Doctor Orders
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'history' }" href="javascript:void(0);" @click="activeTab = 'history'">
                            <i class="mdi mdi-history me-1"></i> History
                          </a>
                        </li>
                      </ul>

                      <!-- Vitals Tab -->
                      <div v-if="activeTab === 'vitals'" class="tab-content pt-4">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h5 class="mb-0">Vital Signs</h5>
                          <button class="btn btn-primary" @click="openVitalsModal">
                            <i class="mdi mdi-plus me-1"></i> Record Vitals
                          </button>
                        </div>

                        <!-- Latest Vitals Cards -->
                        <div v-if="latestVitals" class="row mb-4">
                          <div class="col-md-2 col-sm-4 col-6 mb-3">
                            <div class="card border mb-0 h-100">
                              <div class="card-body text-center p-3">
                                <i class="mdi mdi-thermometer text-danger font-size-24"></i>
                                <h4 class="mb-0 mt-2">{{ latestVitals.temperature || '-' }}</h4>
                                <small class="text-muted">Temperature °C</small>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-2 col-sm-4 col-6 mb-3">
                            <div class="card border mb-0 h-100">
                              <div class="card-body text-center p-3">
                                <i class="mdi mdi-heart-pulse text-danger font-size-24"></i>
                                <h4 class="mb-0 mt-2">{{ latestVitals.pulse || '-' }}</h4>
                                <small class="text-muted">Pulse /min</small>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-2 col-sm-4 col-6 mb-3">
                            <div class="card border mb-0 h-100">
                              <div class="card-body text-center p-3">
                                <i class="mdi mdi-gauge text-primary font-size-24"></i>
                                <h4 class="mb-0 mt-2">{{ latestVitals.bloodPressure || '-' }}</h4>
                                <small class="text-muted">BP mmHg</small>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-2 col-sm-4 col-6 mb-3">
                            <div class="card border mb-0 h-100">
                              <div class="card-body text-center p-3">
                                <i class="mdi mdi-lungs text-info font-size-24"></i>
                                <h4 class="mb-0 mt-2">{{ latestVitals.respiratoryRate || '-' }}</h4>
                                <small class="text-muted">Resp /min</small>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-2 col-sm-4 col-6 mb-3">
                            <div class="card border mb-0 h-100">
                              <div class="card-body text-center p-3">
                                <i class="mdi mdi-percent text-success font-size-24"></i>
                                <h4 class="mb-0 mt-2">{{ latestVitals.oxygenSaturation || '-' }}</h4>
                                <small class="text-muted">SpO2 %</small>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-2 col-sm-4 col-6 mb-3">
                            <div class="card border mb-0 h-100">
                              <div class="card-body text-center p-3">
                                <i class="mdi mdi-weight text-warning font-size-24"></i>
                                <h4 class="mb-0 mt-2">{{ latestVitals.weight || '-' }}</h4>
                                <small class="text-muted">Weight kg</small>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Vitals History Table -->
                        <div class="table-responsive">
                          <table class="table table-hover align-middle">
                            <thead class="table-light">
                            <tr>
                              <th>Date/Time</th>
                              <th>Temp °C</th>
                              <th>Pulse</th>
                              <th>BP</th>
                              <th>Resp</th>
                              <th>SpO2</th>
                              <th>Weight</th>
                              <th>Recorded By</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="vital in vitals" :key="vital.vitalID">
                              <td>{{ formatDateTime(vital.recordedAt || vital.dateCreated) }}</td>
                              <td :class="getTempClass(vital.temperature)">{{ vital.temperature || '-' }}</td>
                              <td>{{ vital.pulse || vital.pulseRate || '-' }}</td>
                              <td>{{ vital.bloodPressure || '-' }}</td>
                              <td>{{ vital.respiratoryRate || '-' }}</td>
                              <td :class="getSpO2Class(vital.oxygenSaturation)">{{ vital.oxygenSaturation || '-' }}</td>
                              <td>{{ vital.weight || '-' }}</td>
                              <td>{{ vital.recordedByName || vital.recordedBy || '-' }}</td>
                            </tr>
                            <tr v-if="vitals.length === 0">
                              <td colspan="8" class="text-center py-4 text-muted">
                                <i class="mdi mdi-heart-pulse font-size-48 d-block mb-2"></i>
                                No vitals recorded yet
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- Medications Tab -->
                      <div v-if="activeTab === 'medications'" class="tab-content pt-4">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h5 class="mb-0">Medications & Administration</h5>
                          <button class="btn btn-primary" @click="openMedicationModal">
                            <i class="mdi mdi-plus me-1"></i> Add Medication
                          </button>
                        </div>

                        <div class="table-responsive">
                          <table class="table table-hover align-middle">
                            <thead class="table-light">
                            <tr>
                              <th>Medication</th>
                              <th>Dosage</th>
                              <th>Frequency</th>
                              <th>Route</th>
                              <th>Price</th>
                              <th>Status</th>
                              <th>Billing</th>
                              <th class="text-center">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="med in medications" :key="med.medicationID">
                              <td>
                                <strong>{{ med.drugName }}</strong>
                                <small v-if="med.instructions" class="text-muted d-block">{{ med.instructions }}</small>
                              </td>
                              <td>{{ med.dosage }}</td>
                              <td>{{ med.frequency }}</td>
                              <td>{{ med.route }}</td>
                              <td>
                                <span v-if="med.unitPrice > 0">KES {{ numberFormat(med.unitPrice) }}</span>
                                <span v-else class="text-muted">-</span>
                              </td>
                              <td>
                                <span class="badge" :class="med.status === 'Active' ? 'bg-success' : 'bg-secondary'">
                                  {{ med.status }}
                                </span>
                              </td>
                              <td>
                                <span v-if="med.addedToBill === 'Yes'" class="badge bg-info">
                                  <i class="mdi mdi-check"></i> Added
                                </span>
                                <button v-else-if="med.billerFeeID" class="btn btn-sm btn-outline-success" @click="addMedicationToBill(med)" title="Add to Bill">
                                  <i class="mdi mdi-cart-plus"></i>
                                </button>
                                <span v-else class="text-muted small">No price</span>
                              </td>
                              <td class="text-center">
                                <button v-if="med.status === 'Active'" class="btn btn-sm btn-soft-primary me-1" @click="administerMedication(med)" title="Administer">
                                  <i class="mdi mdi-needle"></i>
                                </button>
                                <button v-if="med.status === 'Active'" class="btn btn-sm btn-soft-danger" @click="discontinueMedication(med)" title="Discontinue">
                                  <i class="mdi mdi-close"></i>
                                </button>
                              </td>
                            </tr>
                            <tr v-if="medications.length === 0">
                              <td colspan="8" class="text-center py-4 text-muted">
                                <i class="mdi mdi-pill font-size-48 d-block mb-2"></i>
                                No medications prescribed
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- Nursing Notes Tab -->
                      <div v-if="activeTab === 'notes'" class="tab-content pt-4">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h5 class="mb-0">Nursing Notes</h5>
                          <button class="btn btn-primary" @click="openNoteModal">
                            <i class="mdi mdi-plus me-1"></i> Add Note
                          </button>
                        </div>

                        <div class="timeline">
                          <div v-for="note in nursingNotes" :key="note.noteID" class="timeline-item mb-3">
                            <div class="card border-start border-primary border-3 mb-0">
                              <div class="card-body">
                                <div class="d-flex justify-content-between align-items-start mb-2">
                                  <div>
                                    <span class="badge" :class="getNoteTypeBadge(note.noteType)">{{ note.noteType }}</span>
                                    <small class="text-muted ms-2">{{ formatDateTime(note.createdAt) }}</small>
                                  </div>
                                  <small class="text-muted">{{ note.createdByName }}</small>
                                </div>
                                <p class="mb-0">{{ note.noteContent }}</p>
                              </div>
                            </div>
                          </div>
                          <div v-if="nursingNotes.length === 0" class="text-center py-4 text-muted">
                            <i class="mdi mdi-clipboard-text font-size-48 d-block mb-2"></i>
                            No nursing notes yet
                          </div>
                        </div>
                      </div>

                      <!-- Doctor Orders Tab -->
                      <div v-if="activeTab === 'orders'" class="tab-content pt-4">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h5 class="mb-0">Doctor's Orders</h5>
                          <button class="btn btn-primary" @click="openOrderModal">
                            <i class="mdi mdi-plus me-1"></i> Add Order
                          </button>
                        </div>

                        <div class="table-responsive">
                          <table class="table table-hover align-middle">
                            <thead class="table-light">
                            <tr>
                              <th>Date/Time</th>
                              <th>Order Type</th>
                              <th>Order Details</th>
                              <th>Price</th>
                              <th>Ordered By</th>
                              <th>Status</th>
                              <th>Billing</th>
                              <th class="text-center">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="order in doctorOrders" :key="order.orderID">
                              <td>{{ formatDateTime(order.createdAt) }}</td>
                              <td><span class="badge bg-light text-dark">{{ order.orderType }}</span></td>
                              <td>
                                <span>{{ order.orderDetails }}</span>
                                <small v-if="order.serviceName && order.serviceName !== order.orderDetails" class="text-muted d-block">{{ order.serviceName }}</small>
                              </td>
                              <td>
                                <span v-if="order.unitPrice > 0">KES {{ numberFormat(order.unitPrice) }}</span>
                                <span v-else class="text-muted">-</span>
                              </td>
                              <td>{{ order.orderedByName }}</td>
                              <td>
                                <span class="badge" :class="getOrderStatusBadge(order.status)">{{ order.status }}</span>
                              </td>
                              <td>
                                <span v-if="order.addedToBill === 'Yes'" class="badge bg-info">
                                  <i class="mdi mdi-check"></i> Added
                                </span>
                                <button v-else-if="order.billerFeeID && order.status === 'Completed'" class="btn btn-sm btn-outline-success" @click="addOrderToBill(order)" title="Add to Bill">
                                  <i class="mdi mdi-cart-plus"></i>
                                </button>
                                <span v-else-if="order.status !== 'Completed'" class="text-muted small">Complete first</span>
                                <span v-else class="text-muted small">No price</span>
                              </td>
                              <td class="text-center">
                                <button v-if="order.status === 'Pending'" class="btn btn-sm btn-soft-success" @click="completeOrder(order)" title="Mark Complete">
                                  <i class="mdi mdi-check"></i>
                                </button>
                              </td>
                            </tr>
                            <tr v-if="doctorOrders.length === 0">
                              <td colspan="8" class="text-center py-4 text-muted">
                                <i class="mdi mdi-stethoscope font-size-48 d-block mb-2"></i>
                                No doctor's orders
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- History Tab -->
                      <div v-if="activeTab === 'history'" class="tab-content pt-4">
                        <div class="row">
                          <div class="col-md-6">
                            <div class="card border">
                              <div class="card-header bg-light">
                                <h6 class="mb-0"><i class="mdi mdi-information me-2"></i>Admission Details</h6>
                              </div>
                              <div class="card-body">
                                <table class="table table-sm table-borderless">
                                  <tr>
                                    <td class="text-muted" width="40%">Admission No:</td>
                                    <td><code>{{ admission.admissionNo || admission.admissionNumber }}</code></td>
                                  </tr>
                                  <tr>
                                    <td class="text-muted">Admission Date:</td>
                                    <td>{{ formatDate(admission.admissionDate) }}</td>
                                  </tr>
                                  <tr>
                                    <td class="text-muted">Admission Type:</td>
                                    <td>{{ admission.admissionType }}</td>
                                  </tr>
                                  <tr>
                                    <td class="text-muted">Admitting Doctor:</td>
                                    <td>{{ admission.admittingDoctorName }}</td>
                                  </tr>
                                  <tr>
                                    <td class="text-muted">Ward:</td>
                                    <td>{{ admission.wardName }}</td>
                                  </tr>
                                  <tr>
                                    <td class="text-muted">Bed:</td>
                                    <td>{{ admission.bedNumber }}</td>
                                  </tr>
                                </table>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="card border">
                              <div class="card-header bg-light">
                                <h6 class="mb-0"><i class="mdi mdi-medical-bag me-2"></i>Clinical Information</h6>
                              </div>
                              <div class="card-body">
                                <p class="mb-2"><strong>Reason for Admission:</strong></p>
                                <p class="text-muted">{{ admission.admissionReason || '-' }}</p>
                                <p class="mb-2"><strong>Diagnosis:</strong></p>
                                <p class="text-muted">{{ admission.admittingDiagnosis || admission.diagnosis || '-' }}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- Record Vitals Modal -->
    <div class="modal fade" id="vitalsModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-primary text-white">
            <h5 class="modal-title"><i class="mdi mdi-heart-pulse me-2"></i>Record Vitals</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-4 mb-3">
                <label class="form-label">Temperature (°C)</label>
                <input type="number" step="0.1" class="form-control" v-model="vitalsForm.temperature" placeholder="e.g., 36.5">
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Pulse (per min)</label>
                <input type="number" class="form-control" v-model="vitalsForm.pulse" placeholder="e.g., 72">
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Blood Pressure (mmHg)</label>
                <input type="text" class="form-control" v-model="vitalsForm.bloodPressure" placeholder="e.g., 120/80">
              </div>
            </div>
            <div class="row">
              <div class="col-md-4 mb-3">
                <label class="form-label">Respiratory Rate (per min)</label>
                <input type="number" class="form-control" v-model="vitalsForm.respiratoryRate" placeholder="e.g., 16">
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Oxygen Saturation (%)</label>
                <input type="number" class="form-control" v-model="vitalsForm.oxygenSaturation" placeholder="e.g., 98">
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Weight (kg)</label>
                <input type="number" step="0.1" class="form-control" v-model="vitalsForm.weight" placeholder="e.g., 70">
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Notes</label>
              <textarea class="form-control" rows="2" v-model="vitalsForm.notes" placeholder="Additional observations..."></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveVitals" :disabled="savingVitals">
              <span v-if="savingVitals"><i class="mdi mdi-loading mdi-spin me-1"></i> Saving...</span>
              <span v-else><i class="mdi mdi-check me-1"></i> Save Vitals</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Nursing Note Modal -->
    <div class="modal fade" id="noteModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-info text-white">
            <h5 class="modal-title"><i class="mdi mdi-clipboard-text me-2"></i>Add Nursing Note</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Note Type</label>
              <select class="form-select" v-model="noteForm.noteType">
                <option value="General">General</option>
                <option value="Assessment">Assessment</option>
                <option value="Intervention">Intervention</option>
                <option value="Observation">Observation</option>
                <option value="Handover">Handover</option>
                <option value="Incident">Incident</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Note Content <span class="text-danger">*</span></label>
              <textarea class="form-control" rows="4" v-model="noteForm.noteContent" placeholder="Enter nursing note..."></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-info" @click="saveNote" :disabled="!noteForm.noteContent || savingNote">
              <span v-if="savingNote"><i class="mdi mdi-loading mdi-spin me-1"></i> Saving...</span>
              <span v-else><i class="mdi mdi-check me-1"></i> Save Note</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Doctor Order Modal - UPDATED with Fees & Charges -->
    <div class="modal fade" id="orderModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-warning text-dark">
            <h5 class="modal-title"><i class="mdi mdi-stethoscope me-2"></i>Add Doctor's Order</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Order Type <span class="text-danger">*</span></label>
                <select class="form-select" v-model="orderForm.orderType" @change="onOrderTypeChange">
                  <option value="">-- Select Type --</option>
                  <option value="Lab Test">Lab Test</option>
                  <option value="Imaging">Imaging / Radiology</option>
                  <option value="Procedure">Procedure</option>
                  <option value="Consultation">Consultation</option>
                  <option value="Medication">Medication</option>
                  <option value="Diet">Diet</option>
                  <option value="Activity">Activity</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Priority</label>
                <select class="form-select" v-model="orderForm.priority">
                  <option value="Routine">Routine</option>
                  <option value="Urgent">Urgent</option>
                  <option value="STAT">STAT</option>
                </select>
              </div>
            </div>

            <!-- Category & Service Selection (for billable orders) -->
            <div v-if="isBillableOrderType" class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Category</label>
                <select class="form-select" v-model="orderSelectedIncomeType" @change="onOrderIncomeTypeChange">
                  <option value="">-- Select Category --</option>
                  <option v-for="item in orderIncomeTypes" :key="item.incomeTypeId" :value="item.incomeTypeId">
                    {{ item.incomeTypeDescription }}
                  </option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Service / Test</label>
                <select class="form-select" v-model="orderSelectedFee" @change="onOrderFeeChange" :disabled="!orderFeesAndCharges.length">
                  <option value="">-- Select Service --</option>
                  <option v-for="item in orderFeesAndCharges" :key="item.feeId" :value="item.feeId">
                    {{ item.feeDescription }} - KES {{ numberFormat(item.unitFeeAmount) }}
                  </option>
                </select>
                <div v-if="loadingOrderFees" class="text-muted small mt-1">
                  <i class="mdi mdi-loading mdi-spin"></i> Loading services...
                </div>
              </div>
            </div>

            <!-- Selected Service Info -->
            <div v-if="orderSelectedFeeData" class="alert alert-info mb-3">
              <div class="d-flex justify-content-between">
                <div>
                  <strong>{{ orderSelectedFeeData.feeDescription }}</strong>
                  <br><small class="text-muted">{{ orderSelectedFeeData.accountDesc }}</small>
                </div>
                <div class="text-end">
                  <h5 class="mb-0 text-primary">KES {{ numberFormat(orderSelectedFeeData.unitFeeAmount) }}</h5>
                </div>
              </div>
            </div>

            <!-- Manual Entry Option -->
            <div v-if="isBillableOrderType" class="mb-3">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="orderManualEntry" id="orderManualEntryCheck">
                <label class="form-check-label" for="orderManualEntryCheck">
                  Enter manually (if not in list)
                </label>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Order Details <span class="text-danger">*</span></label>
              <textarea class="form-control" rows="3" v-model="orderForm.orderDetails" :placeholder="orderDetailsPlaceholder"></textarea>
            </div>

            <div v-if="isBillableOrderType && (orderManualEntry || !orderSelectedFeeData)" class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Quantity</label>
                <input type="number" class="form-control" v-model="orderForm.quantity" min="1" placeholder="1">
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Unit Price (KES)</label>
                <input type="number" class="form-control" v-model="orderForm.unitPrice" placeholder="0">
              </div>
            </div>

            <!-- Estimated Cost -->
            <div v-if="orderEstimatedCost > 0" class="alert alert-success">
              <div class="d-flex justify-content-between align-items-center">
                <span><i class="mdi mdi-calculator me-2"></i>Estimated Cost:</span>
                <strong class="font-size-18">KES {{ numberFormat(orderEstimatedCost) }}</strong>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-warning" @click="saveOrder" :disabled="!canSaveOrder || savingOrder">
              <span v-if="savingOrder"><i class="mdi mdi-loading mdi-spin me-1"></i> Saving...</span>
              <span v-else><i class="mdi mdi-check me-1"></i> Save Order</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Medication Modal - UPDATED with Fees & Charges -->
    <div class="modal fade" id="medicationModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-success text-white">
            <h5 class="modal-title"><i class="mdi mdi-pill me-2"></i>Add Medication</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <!-- Medication Selection from Fees & Charges -->
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Medication Category <span class="text-danger">*</span></label>
                <select class="form-select" v-model="selectedIncomeType" @change="onIncomeTypeChange">
                  <option value="">-- Select Category --</option>
                  <option v-for="item in pharmacyIncomeTypes" :key="item.incomeTypeId" :value="item.incomeTypeId">
                    {{ item.incomeTypeDescription }}
                  </option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Medication <span class="text-danger">*</span></label>
                <select class="form-select" v-model="selectedFee" @change="onFeeChange" :disabled="!feesAndCharges.length">
                  <option value="">-- Select Medication --</option>
                  <option v-for="item in feesAndCharges" :key="item.feeId" :value="item.feeId">
                    {{ item.feeDescription }} - KES {{ numberFormat(item.unitFeeAmount) }}
                  </option>
                </select>
                <div v-if="loadingFees" class="text-muted small mt-1">
                  <i class="mdi mdi-loading mdi-spin"></i> Loading medications...
                </div>
              </div>
            </div>

            <!-- Selected Medication Info -->
            <div v-if="selectedFeeData" class="alert alert-info mb-3">
              <div class="d-flex justify-content-between">
                <div>
                  <strong>{{ selectedFeeData.feeDescription }}</strong>
                  <br><small class="text-muted">{{ selectedFeeData.accountDesc }}</small>
                </div>
                <div class="text-end">
                  <h5 class="mb-0 text-primary">KES {{ numberFormat(selectedFeeData.unitFeeAmount) }}</h5>
                  <small>per unit</small>
                </div>
              </div>
            </div>

            <!-- Or Manual Entry -->
            <div class="mb-3">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="manualEntry" id="manualEntryCheck">
                <label class="form-check-label" for="manualEntryCheck">
                  Enter medication manually (if not in list)
                </label>
              </div>
            </div>

            <div v-if="manualEntry" class="row">
              <div class="col-md-8 mb-3">
                <label class="form-label">Drug Name <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model="medicationForm.drugName" placeholder="Enter drug name">
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Unit Price</label>
                <input type="number" class="form-control" v-model="medicationForm.unitPrice" placeholder="0">
              </div>
            </div>

            <hr>

            <!-- Dosage & Administration -->
            <div class="row">
              <div class="col-md-4 mb-3">
                <label class="form-label">Dosage <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model="medicationForm.dosage" placeholder="e.g., 500mg, 10ml">
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Frequency <span class="text-danger">*</span></label>
                <select class="form-select" v-model="medicationForm.frequency">
                  <option value="">-- Select --</option>
                  <option value="Once Daily (OD)">Once Daily (OD)</option>
                  <option value="Twice Daily (BD)">Twice Daily (BD)</option>
                  <option value="Three Times Daily (TDS)">Three Times Daily (TDS)</option>
                  <option value="Four Times Daily (QID)">Four Times Daily (QID)</option>
                  <option value="Every 4 Hours (Q4H)">Every 4 Hours (Q4H)</option>
                  <option value="Every 6 Hours (Q6H)">Every 6 Hours (Q6H)</option>
                  <option value="Every 8 Hours (Q8H)">Every 8 Hours (Q8H)</option>
                  <option value="Every 12 Hours (Q12H)">Every 12 Hours (Q12H)</option>
                  <option value="At Bedtime (HS)">At Bedtime (HS)</option>
                  <option value="As Needed (PRN)">As Needed (PRN)</option>
                  <option value="Once Weekly">Once Weekly</option>
                  <option value="STAT">STAT (Immediately)</option>
                </select>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Route <span class="text-danger">*</span></label>
                <select class="form-select" v-model="medicationForm.route">
                  <option value="">-- Select --</option>
                  <option value="Oral (PO)">Oral (PO)</option>
                  <option value="Intravenous (IV)">Intravenous (IV)</option>
                  <option value="Intramuscular (IM)">Intramuscular (IM)</option>
                  <option value="Subcutaneous (SC)">Subcutaneous (SC)</option>
                  <option value="Topical">Topical</option>
                  <option value="Inhalation">Inhalation</option>
                  <option value="Sublingual (SL)">Sublingual (SL)</option>
                  <option value="Rectal (PR)">Rectal (PR)</option>
                  <option value="Ophthalmic">Ophthalmic (Eye)</option>
                  <option value="Otic">Otic (Ear)</option>
                  <option value="Nasal">Nasal</option>
                  <option value="Transdermal">Transdermal (Patch)</option>
                </select>
              </div>
            </div>

            <div class="row">
              <div class="col-md-3 mb-3">
                <label class="form-label">Quantity</label>
                <input type="number" class="form-control" v-model="medicationForm.quantity" min="1" placeholder="1">
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">Duration (Days)</label>
                <input type="number" class="form-control" v-model="medicationForm.duration" min="1" placeholder="e.g., 7">
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">Start Date <span class="text-danger">*</span></label>
                <input type="date" class="form-control" v-model="medicationForm.startDate">
              </div>
              <div class="col-md-3 mb-3">
                <label class="form-label">End Date</label>
                <input type="date" class="form-control" v-model="medicationForm.endDate">
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Special Instructions</label>
              <textarea class="form-control" rows="2" v-model="medicationForm.instructions" placeholder="e.g., Take with food, Avoid alcohol..."></textarea>
            </div>

            <!-- Estimated Cost -->
            <div v-if="estimatedCost > 0" class="alert alert-success">
              <div class="d-flex justify-content-between align-items-center">
                <span><i class="mdi mdi-calculator me-2"></i>Estimated Cost:</span>
                <strong class="font-size-18">KES {{ numberFormat(estimatedCost) }}</strong>
              </div>
              <small class="text-muted">{{ medicationForm.quantity || 1 }} unit(s) × KES {{ numberFormat(medicationForm.unitPrice) }}</small>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-success" @click="saveMedication" :disabled="!canSaveMedication || savingMedication">
              <span v-if="savingMedication"><i class="mdi mdi-loading mdi-spin me-1"></i> Saving...</span>
              <span v-else><i class="mdi mdi-check me-1"></i> Add Medication</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Administer Medication Modal -->
    <div class="modal fade" id="administerModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-primary text-white">
            <h5 class="modal-title"><i class="mdi mdi-needle me-2"></i>Administer Medication</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedMedication" class="alert alert-info mb-3">
              <strong>{{ selectedMedication.drugName }}</strong><br>
              <small>{{ selectedMedication.dosage }} - {{ selectedMedication.frequency }} - {{ selectedMedication.route }}</small>
            </div>
            <div class="mb-3">
              <label class="form-label">Administration Time <span class="text-danger">*</span></label>
              <input type="datetime-local" class="form-control" v-model="administerForm.administeredAt">
            </div>
            <div class="mb-3">
              <label class="form-label">Dose Given</label>
              <input type="text" class="form-control" v-model="administerForm.doseGiven" :placeholder="selectedMedication?.dosage">
            </div>
            <div class="mb-3">
              <label class="form-label">Notes</label>
              <textarea class="form-control" rows="2" v-model="administerForm.notes" placeholder="Any observations or notes..."></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveAdministration" :disabled="!administerForm.administeredAt || administeringSaving">
              <span v-if="administeringSaving"><i class="mdi mdi-loading mdi-spin me-1"></i> Saving...</span>
              <span v-else><i class="mdi mdi-check me-1"></i> Record Administration</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, hospital, biller } from "@/api";

export default {
  name: "PatientCare",
  components: { NavigationBar, ToastNotification },
  data() {
    return {
      loading: true,
      admissionID: '',
      admission: null,
      activeTab: 'vitals',

      // Data
      vitals: [],
      medications: [],
      nursingNotes: [],
      doctorOrders: [],

      // Income Types & Fees (for medication picker)
      incomeTypes: [],
      pharmacyIncomeTypes: [],
      feesAndCharges: [],
      selectedIncomeType: '',
      selectedFee: '',
      selectedFeeData: null,
      loadingFees: false,
      manualEntry: false,

      // Income Types & Fees (for doctor orders)
      orderIncomeTypes: [],
      orderFeesAndCharges: [],
      orderSelectedIncomeType: '',
      orderSelectedFee: '',
      orderSelectedFeeData: null,
      loadingOrderFees: false,
      orderManualEntry: false,

      // Forms
      vitalsForm: {
        temperature: '',
        pulse: '',
        bloodPressure: '',
        respiratoryRate: '',
        oxygenSaturation: '',
        weight: '',
        notes: ''
      },
      noteForm: {
        noteType: 'General',
        noteContent: ''
      },
      orderForm: {
        orderType: '',
        orderDetails: '',
        priority: 'Routine',
        quantity: 1,
        unitPrice: 0,
        billerFeeID: '',
        billerIncomeTypeID: '',
        serviceName: ''
      },
      medicationForm: {
        drugName: '',
        dosage: '',
        frequency: '',
        route: '',
        duration: '',
        quantity: 1,
        unitPrice: 0,
        startDate: '',
        endDate: '',
        instructions: '',
        billerFeeID: '',
        billerIncomeTypeID: ''
      },
      administerForm: {
        administeredAt: '',
        doseGiven: '',
        notes: ''
      },
      selectedMedication: null,

      // State
      savingVitals: false,
      savingNote: false,
      savingOrder: false,
      savingMedication: false,
      administeringSaving: false,

      // Session
      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: ''
    };
  },
  computed: {
    latestVitals() {
      if (this.vitals.length === 0) return null;
      const v = this.vitals[0];
      return {
        temperature: v.temperature,
        pulse: v.pulse || v.pulseRate,
        bloodPressure: v.bloodPressure,
        respiratoryRate: v.respiratoryRate,
        oxygenSaturation: v.oxygenSaturation,
        weight: v.weight
      };
    },
    todayVitalsCount() {
      const today = new Date().toISOString().split('T')[0];
      return this.vitals.filter(v => {
        const recordDate = v.recordedAt || v.dateCreated;
        return recordDate && recordDate.startsWith(today);
      }).length;
    },
    canSaveMedication() {
      const hasDrug = this.manualEntry ? this.medicationForm.drugName : this.selectedFeeData;
      return hasDrug &&
          this.medicationForm.dosage &&
          this.medicationForm.frequency &&
          this.medicationForm.route &&
          this.medicationForm.startDate;
    },
    estimatedCost() {
      const qty = parseFloat(this.medicationForm.quantity) || 1;
      const price = parseFloat(this.medicationForm.unitPrice) || 0;
      return qty * price;
    },
    isBillableOrderType() {
      return ['Lab Test', 'Imaging', 'Procedure', 'Consultation'].includes(this.orderForm.orderType);
    },
    orderDetailsPlaceholder() {
      if (this.orderSelectedFeeData) {
        return this.orderSelectedFeeData.feeDescription + ' - Add any special instructions...';
      }
      const placeholders = {
        'Lab Test': 'e.g., CBC, Lipid Panel, Blood Sugar...',
        'Imaging': 'e.g., Chest X-Ray, Abdominal Ultrasound...',
        'Procedure': 'e.g., Wound dressing, IV cannulation...',
        'Consultation': 'e.g., Cardiology consult for chest pain...',
        'Medication': 'e.g., Start IV antibiotics...',
        'Diet': 'e.g., NPO, Diabetic diet, Low sodium...',
        'Activity': 'e.g., Bed rest, Ambulate with assistance...',
        'Other': 'Enter order details...'
      };
      return placeholders[this.orderForm.orderType] || 'Enter order details...';
    },
    orderEstimatedCost() {
      const qty = parseFloat(this.orderForm.quantity) || 1;
      const price = parseFloat(this.orderForm.unitPrice) || 0;
      return qty * price;
    },
    canSaveOrder() {
      return this.orderForm.orderType && this.orderForm.orderDetails;
    }
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    numberFormat(num) {
      return new Intl.NumberFormat('en-GB').format(num || 0);
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatDateTime(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },

    getTempClass(temp) {
      if (!temp) return '';
      const t = parseFloat(temp);
      if (t >= 38) return 'text-danger fw-bold';
      if (t < 36) return 'text-warning fw-bold';
      return '';
    },

    getSpO2Class(spo2) {
      if (!spo2) return '';
      const s = parseInt(spo2);
      if (s < 90) return 'text-danger fw-bold';
      if (s < 95) return 'text-warning fw-bold';
      return 'text-success';
    },

    getNoteTypeBadge(type) {
      const badges = {
        'General': 'bg-secondary',
        'Assessment': 'bg-primary',
        'Intervention': 'bg-success',
        'Observation': 'bg-info',
        'Handover': 'bg-warning text-dark',
        'Incident': 'bg-danger'
      };
      return badges[type] || 'bg-secondary';
    },

    getOrderStatusBadge(status) {
      const badges = {
        'Pending': 'bg-warning text-dark',
        'In Progress': 'bg-info',
        'Completed': 'bg-success',
        'Cancelled': 'bg-secondary'
      };
      return badges[status] || 'bg-secondary';
    },

    // Load Income Types for Pharmacy
    loadIncomeTypes() {
      const data = new FormData();
      data.append("function", "getIncomeTypes");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.incomeTypes = res.data.data.incomeTypes || [];
              // Filter pharmacy-related income types (IDs 154-165 are pharmacy/medical)
              this.pharmacyIncomeTypes = this.incomeTypes.filter(item => {
                const desc = (item.incomeTypeDescription || '').toLowerCase();
                return desc.includes('pharmacy') ||
                    desc.includes('vaccine') ||
                    desc.includes('nutrition') ||
                    desc.includes('medical oxygen') ||
                    item.incomeTypePrefix === 'MEDICAL';
              });
            }
          })
          .catch((e) => console.error(e));
    },

    // Get income types based on order type
    onOrderTypeChange() {
      this.orderSelectedIncomeType = '';
      this.orderSelectedFee = '';
      this.orderSelectedFeeData = null;
      this.orderFeesAndCharges = [];
      this.orderManualEntry = false;
      this.orderForm.billerFeeID = '';
      this.orderForm.billerIncomeTypeID = '';
      this.orderForm.unitPrice = 0;
      this.orderForm.serviceName = '';

      if (this.isBillableOrderType) {
        this.loadOrderIncomeTypes();
      }
    },

    loadOrderIncomeTypes() {
      // Filter income types based on order type
      const typeFilters = {
        'Lab Test': ['lab', 'laboratory', 'pathology'],
        'Imaging': ['radiology', 'imaging', 'x-ray', 'scan', 'ultrasound'],
        'Procedure': ['procedure', 'theatre', 'surgery', 'minor'],
        'Consultation': ['consultation', 'consult', 'specialist']
      };

      const filters = typeFilters[this.orderForm.orderType] || [];

      this.orderIncomeTypes = this.incomeTypes.filter(item => {
        const desc = (item.incomeTypeDescription || '').toLowerCase();
        return filters.some(f => desc.includes(f));
      });

      // If no matches, show all medical-related
      if (this.orderIncomeTypes.length === 0) {
        this.orderIncomeTypes = this.incomeTypes.filter(item => {
          const prefix = (item.incomeTypePrefix || '').toUpperCase();
          return prefix === 'MEDICAL' || prefix === 'HEALTH';
        });
      }
    },

    onOrderIncomeTypeChange() {
      this.orderFeesAndCharges = [];
      this.orderSelectedFee = '';
      this.orderSelectedFeeData = null;

      if (this.orderSelectedIncomeType) {
        this.loadOrderFeesAndCharges(this.orderSelectedIncomeType);
      }
    },

    loadOrderFeesAndCharges(incomeTypeId) {
      this.loadingOrderFees = true;
      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", incomeTypeId);

      execute(data, biller)
          .then((res) => {
            this.loadingOrderFees = false;
            if (res.data.success) {
              this.orderFeesAndCharges = res.data.data.feesAndCharges || [];
            }
          })
          .catch((e) => {
            this.loadingOrderFees = false;
            console.error(e);
          });
    },

    onOrderFeeChange() {
      if (this.orderSelectedFee) {
        this.orderSelectedFeeData = this.orderFeesAndCharges.find(f => f.feeId == this.orderSelectedFee);
        if (this.orderSelectedFeeData) {
          this.orderForm.serviceName = this.orderSelectedFeeData.feeDescription;
          this.orderForm.unitPrice = parseFloat(this.orderSelectedFeeData.unitFeeAmount) || 0;
          this.orderForm.billerFeeID = this.orderSelectedFeeData.feeId;
          this.orderForm.billerIncomeTypeID = this.orderSelectedIncomeType;
          // Auto-fill order details if empty
          if (!this.orderForm.orderDetails) {
            this.orderForm.orderDetails = this.orderSelectedFeeData.feeDescription;
          }
        }
      } else {
        this.orderSelectedFeeData = null;
        this.orderForm.serviceName = '';
        this.orderForm.unitPrice = 0;
        this.orderForm.billerFeeID = '';
        this.orderForm.billerIncomeTypeID = '';
      }
    },

    onIncomeTypeChange() {
      this.feesAndCharges = [];
      this.selectedFee = '';
      this.selectedFeeData = null;

      if (this.selectedIncomeType) {
        this.loadFeesAndCharges(this.selectedIncomeType);
      }
    },

    loadFeesAndCharges(incomeTypeId) {
      this.loadingFees = true;
      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", incomeTypeId);

      execute(data, biller)
          .then((res) => {
            this.loadingFees = false;
            if (res.data.success) {
              this.feesAndCharges = res.data.data.feesAndCharges || [];
            }
          })
          .catch((e) => {
            this.loadingFees = false;
            console.error(e);
          });
    },

    onFeeChange() {
      if (this.selectedFee) {
        this.selectedFeeData = this.feesAndCharges.find(f => f.feeId == this.selectedFee);
        if (this.selectedFeeData) {
          this.medicationForm.drugName = this.selectedFeeData.feeDescription;
          this.medicationForm.unitPrice = parseFloat(this.selectedFeeData.unitFeeAmount) || 0;
          this.medicationForm.billerFeeID = this.selectedFeeData.feeId;
          this.medicationForm.billerIncomeTypeID = this.selectedIncomeType;
        }
      } else {
        this.selectedFeeData = null;
        this.medicationForm.drugName = '';
        this.medicationForm.unitPrice = 0;
        this.medicationForm.billerFeeID = '';
        this.medicationForm.billerIncomeTypeID = '';
      }
    },

    // Load Data
    loadAdmission() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getAdmissionDetails");
      data.append("admissionID", this.admissionID);

      execute(data, hospital)
          .then((res) => {
            this.loading = false;
            if (res.data.success && res.data.data.admission) {
              this.admission = res.data.data.admission;
              this.loadVitals();
              this.loadMedications();
              this.loadNursingNotes();
              this.loadDoctorOrders();
            } else {
              this.admission = null;
            }
          })
          .catch((e) => {
            this.loading = false;
            console.error(e);
          });
    },

    loadVitals() {
      const data = new FormData();
      data.append("function", "getAdmissionVitals");
      data.append("admissionID", this.admissionID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.vitals = res.data.data.vitals || [];
            }
          })
          .catch((e) => console.error(e));
    },

    loadMedications() {
      const data = new FormData();
      data.append("function", "getAdmissionMedications");
      data.append("admissionID", this.admissionID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.medications = res.data.data.medications || [];
            }
          })
          .catch((e) => console.error(e));
    },

    loadNursingNotes() {
      const data = new FormData();
      data.append("function", "getNursingNotes");
      data.append("admissionID", this.admissionID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.nursingNotes = res.data.data.notes || [];
            }
          })
          .catch((e) => console.error(e));
    },

    loadDoctorOrders() {
      const data = new FormData();
      data.append("function", "getDoctorOrders");
      data.append("admissionID", this.admissionID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.doctorOrders = res.data.data.orders || [];
            }
          })
          .catch((e) => console.error(e));
    },

    // Modals
    openVitalsModal() {
      this.vitalsForm = {
        temperature: '',
        pulse: '',
        bloodPressure: '',
        respiratoryRate: '',
        oxygenSaturation: '',
        weight: '',
        notes: ''
      };
      const modal = new bootstrap.Modal(document.getElementById('vitalsModal'));
      modal.show();
    },

    openNoteModal() {
      this.noteForm = { noteType: 'General', noteContent: '' };
      const modal = new bootstrap.Modal(document.getElementById('noteModal'));
      modal.show();
    },

    openOrderModal() {
      this.orderSelectedIncomeType = '';
      this.orderSelectedFee = '';
      this.orderSelectedFeeData = null;
      this.orderFeesAndCharges = [];
      this.orderManualEntry = false;
      this.orderForm = {
        orderType: '',
        orderDetails: '',
        priority: 'Routine',
        quantity: 1,
        unitPrice: 0,
        billerFeeID: '',
        billerIncomeTypeID: '',
        serviceName: ''
      };
      const modal = new bootstrap.Modal(document.getElementById('orderModal'));
      modal.show();
    },

    openMedicationModal() {
      // Reset form
      this.selectedIncomeType = '';
      this.selectedFee = '';
      this.selectedFeeData = null;
      this.feesAndCharges = [];
      this.manualEntry = false;
      this.medicationForm = {
        drugName: '',
        dosage: '',
        frequency: '',
        route: '',
        duration: '',
        quantity: 1,
        unitPrice: 0,
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        instructions: '',
        billerFeeID: '',
        billerIncomeTypeID: ''
      };
      const modal = new bootstrap.Modal(document.getElementById('medicationModal'));
      modal.show();
    },

    saveMedication() {
      if (!this.canSaveMedication) return;

      this.savingMedication = true;
      const data = new FormData();
      data.append("function", "addAdmissionMedication");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("admissionID", this.admissionID);
      data.append("patientID", this.admission.patientID);
      data.append("patientName", this.admission.patientName);
      data.append("drugName", this.medicationForm.drugName);
      data.append("dosage", this.medicationForm.dosage);
      data.append("frequency", this.medicationForm.frequency);
      data.append("route", this.medicationForm.route);
      data.append("duration", this.medicationForm.duration);
      data.append("quantity", this.medicationForm.quantity || 1);
      data.append("unitPrice", this.medicationForm.unitPrice || 0);
      data.append("totalPrice", this.estimatedCost);
      data.append("startDate", this.medicationForm.startDate);
      data.append("endDate", this.medicationForm.endDate);
      data.append("instructions", this.medicationForm.instructions);
      data.append("billerFeeID", this.medicationForm.billerFeeID);
      data.append("billerIncomeTypeID", this.medicationForm.billerIncomeTypeID);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.savingMedication = false;
            if (res.data.success) {
              this.$refs.toast.success('Medication added successfully');
              bootstrap.Modal.getInstance(document.getElementById('medicationModal')).hide();
              this.loadMedications();
            } else {
              this.$refs.toast.error(res.data.message || 'Failed to add medication');
            }
          })
          .catch((e) => {
            this.savingMedication = false;
            this.$refs.toast.error('Error adding medication');
          });
    },

    // Add medication to billing
    addMedicationToBill(med) {
      if (!med.billerFeeID) {
        this.$refs.toast.error('No billing info for this medication');
        return;
      }

      const data = new FormData();
      data.append("function", "addVisitCharge");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("admissionID", this.admissionID);
      data.append("patientID", this.admission.patientID);
      data.append("patientName", this.admission.patientName);
      data.append("serviceCategory", "PHARMACY");
      data.append("serviceName", med.drugName);
      data.append("referenceType", "admission_medications");
      data.append("referenceID", med.medicationID);
      data.append("sourceModule", "IPD");
      data.append("quantity", med.quantity || 1);
      data.append("unitPrice", med.unitPrice || 0);
      data.append("billerFeeID", med.billerFeeID);
      data.append("billerIncomeTypeID", med.billerIncomeTypeID || '');
      data.append("sessionNames", this.sessionNames);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              // Update medication record to mark as added to bill
              const updateData = new FormData();
              updateData.append("function", "updateMedicationBillStatus");
              updateData.append("medicationID", med.medicationID);
              updateData.append("visitChargeID", res.data.data.visitChargeID);
              updateData.append("addedToBill", "Yes");

              execute(updateData, hospital).then(() => {
                this.$refs.toast.success('Medication added to bill');
                this.loadMedications();
              });
            } else {
              this.$refs.toast.error(res.data.message || 'Failed to add to bill');
            }
          })
          .catch((e) => {
            this.$refs.toast.error('Error adding to bill');
          });
    },

    // Add doctor order to billing
    addOrderToBill(order) {
      if (!order.billerFeeID) {
        this.$refs.toast.error('No billing info for this order');
        return;
      }

      // Map order type to service category
      const categoryMap = {
        'Lab Test': 'LAB',
        'Imaging': 'RADIOLOGY',
        'Procedure': 'PROCEDURE',
        'Consultation': 'CONSULTATION'
      };

      const data = new FormData();
      data.append("function", "addVisitCharge");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("admissionID", this.admissionID);
      data.append("patientID", this.admission.patientID);
      data.append("patientName", this.admission.patientName);
      data.append("serviceCategory", categoryMap[order.orderType] || 'OTHER');
      data.append("serviceName", order.serviceName || order.orderDetails);
      data.append("referenceType", "doctor_orders");
      data.append("referenceID", order.orderID);
      data.append("sourceModule", "IPD");
      data.append("quantity", order.quantity || 1);
      data.append("unitPrice", order.unitPrice || 0);
      data.append("billerFeeID", order.billerFeeID);
      data.append("billerIncomeTypeID", order.billerIncomeTypeID || '');
      data.append("sessionNames", this.sessionNames);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              // Update order record to mark as added to bill
              const updateData = new FormData();
              updateData.append("function", "updateOrderBillStatus");
              updateData.append("orderID", order.orderID);
              updateData.append("visitChargeID", res.data.data.visitChargeID);
              updateData.append("addedToBill", "Yes");

              execute(updateData, hospital).then(() => {
                this.$refs.toast.success('Order added to bill');
                this.loadDoctorOrders();
              });
            } else {
              this.$refs.toast.error(res.data.message || 'Failed to add to bill');
            }
          })
          .catch((e) => {
            this.$refs.toast.error('Error adding to bill');
          });
    },

    // Save Functions
    saveVitals() {
      this.savingVitals = true;
      const data = new FormData();
      data.append("function", "recordVitals");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("admissionID", this.admissionID);
      data.append("patientID", this.admission.patientID);
      data.append("temperature", this.vitalsForm.temperature);
      data.append("pulse", this.vitalsForm.pulse);
      data.append("bloodPressure", this.vitalsForm.bloodPressure);
      data.append("respiratoryRate", this.vitalsForm.respiratoryRate);
      data.append("oxygenSaturation", this.vitalsForm.oxygenSaturation);
      data.append("weight", this.vitalsForm.weight);
      data.append("notes", this.vitalsForm.notes);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.savingVitals = false;
            if (res.data.success) {
              this.$refs.toast.success('Vitals recorded successfully');
              bootstrap.Modal.getInstance(document.getElementById('vitalsModal')).hide();
              this.loadVitals();
            } else {
              this.$refs.toast.error(res.data.message || 'Failed to record vitals');
            }
          })
          .catch((e) => {
            this.savingVitals = false;
            this.$refs.toast.error('Error recording vitals');
          });
    },

    saveNote() {
      if (!this.noteForm.noteContent) return;

      this.savingNote = true;
      const data = new FormData();
      data.append("function", "addNursingNote");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("admissionID", this.admissionID);
      data.append("patientID", this.admission.patientID);
      data.append("noteType", this.noteForm.noteType);
      data.append("noteContent", this.noteForm.noteContent);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.savingNote = false;
            if (res.data.success) {
              this.$refs.toast.success('Note added successfully');
              bootstrap.Modal.getInstance(document.getElementById('noteModal')).hide();
              this.loadNursingNotes();
            } else {
              this.$refs.toast.error(res.data.message || 'Failed to add note');
            }
          })
          .catch((e) => {
            this.savingNote = false;
            this.$refs.toast.error('Error adding note');
          });
    },

    saveOrder() {
      if (!this.orderForm.orderDetails || !this.orderForm.orderType) return;

      this.savingOrder = true;
      const data = new FormData();
      data.append("function", "addDoctorOrder");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("admissionID", this.admissionID);
      data.append("patientID", this.admission.patientID);
      data.append("patientName", this.admission.patientName);
      data.append("orderType", this.orderForm.orderType);
      data.append("orderDetails", this.orderForm.orderDetails);
      data.append("priority", this.orderForm.priority);
      data.append("serviceName", this.orderForm.serviceName || this.orderForm.orderDetails);
      data.append("quantity", this.orderForm.quantity || 1);
      data.append("unitPrice", this.orderForm.unitPrice || 0);
      data.append("totalPrice", this.orderEstimatedCost);
      data.append("billerFeeID", this.orderForm.billerFeeID);
      data.append("billerIncomeTypeID", this.orderForm.billerIncomeTypeID);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.savingOrder = false;
            if (res.data.success) {
              this.$refs.toast.success('Order added successfully');
              bootstrap.Modal.getInstance(document.getElementById('orderModal')).hide();
              this.loadDoctorOrders();
            } else {
              this.$refs.toast.error(res.data.message || 'Failed to add order');
            }
          })
          .catch((e) => {
            this.savingOrder = false;
            this.$refs.toast.error('Error adding order');
          });
    },

    completeOrder(order) {
      const data = new FormData();
      data.append("function", "updateOrderStatus");
      data.append("orderID", order.orderID);
      data.append("status", "Completed");
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.$refs.toast.success('Order marked as completed');
              this.loadDoctorOrders();
            } else {
              this.$refs.toast.error(res.data.message || 'Failed to update order');
            }
          })
          .catch((e) => {
            this.$refs.toast.error('Error updating order');
          });
    },

    administerMedication(med) {
      this.selectedMedication = med;
      const now = new Date();
      this.administerForm = {
        administeredAt: now.toISOString().slice(0, 16),
        doseGiven: med.dosage,
        notes: ''
      };
      const modal = new bootstrap.Modal(document.getElementById('administerModal'));
      modal.show();
    },

    saveAdministration() {
      if (!this.administerForm.administeredAt) return;

      this.administeringSaving = true;
      const data = new FormData();
      data.append("function", "recordMedicationAdministration");
      data.append("facilityID", this.facilityID);
      data.append("admissionID", this.admissionID);
      data.append("medicationID", this.selectedMedication.medicationID);
      data.append("patientID", this.admission.patientID);
      data.append("drugName", this.selectedMedication.drugName);
      data.append("doseGiven", this.administerForm.doseGiven || this.selectedMedication.dosage);
      data.append("administeredAt", this.administerForm.administeredAt);
      data.append("notes", this.administerForm.notes);
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.administeringSaving = false;
            if (res.data.success) {
              this.$refs.toast.success('Medication administration recorded');
              bootstrap.Modal.getInstance(document.getElementById('administerModal')).hide();
            } else {
              this.$refs.toast.error(res.data.message || 'Failed to record administration');
            }
          })
          .catch((e) => {
            this.administeringSaving = false;
            this.$refs.toast.error('Error recording administration');
          });
    },

    discontinueMedication(med) {
      if (!confirm(`Are you sure you want to discontinue ${med.drugName}?`)) return;

      const data = new FormData();
      data.append("function", "discontinueMedication");
      data.append("medicationID", med.medicationID);
      data.append("reason", "Discontinued by physician");
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.$refs.toast.success('Medication discontinued');
              this.loadMedications();
            } else {
              this.$refs.toast.error(res.data.message || 'Failed to discontinue medication');
            }
          })
          .catch((e) => {
            this.$refs.toast.error('Error discontinuing medication');
          });
    }
  },
  mounted() {
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';

    // Get admission ID from route
    this.admissionID = this.$route.params.id || '';

    // Load income types for medication picker
    this.loadIncomeTypes();

    if (this.admissionID) {
      this.loadAdmission();
    } else {
      this.loading = false;
    }
  }
};
</script>

<style scoped>
.avatar-lg { width: 4rem; height: 4rem; }
.avatar-sm { width: 2.5rem; height: 2.5rem; }
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 500;
}

.mini-stats-wid .card-body { padding: 20px; }
.mini-stat-icon { width: 48px; height: 48px; }

.nav-tabs-custom .nav-link {
  border: none;
  color: #74788d;
}
.nav-tabs-custom .nav-link.active {
  color: #556ee6;
  border-bottom: 2px solid #556ee6;
}

.btn-soft-primary {
  background-color: rgba(85, 110, 230, 0.1);
  color: #556ee6;
  border: none;
}
.btn-soft-primary:hover { background-color: rgba(85, 110, 230, 0.2); }

.btn-soft-success {
  background-color: rgba(52, 195, 143, 0.1);
  color: #34c38f;
  border: none;
}
.btn-soft-success:hover { background-color: rgba(52, 195, 143, 0.2); }

.btn-soft-danger {
  background-color: rgba(244, 106, 106, 0.1);
  color: #f46a6a;
  border: none;
}
.btn-soft-danger:hover { background-color: rgba(244, 106, 106, 0.2); }

.timeline-item {
  position: relative;
  padding-left: 20px;
}
.timeline-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e9ecef;
}
</style>